import { useEffect, useState } from "react";
import axios from "axios";
import { Mail, Inbox, Phone, ShoppingBag, Download, Truck, Package, Copy } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../components/motion";
import { API_URL } from "../config/site";

const api = axios.create({ baseURL: API_URL, withCredentials: true });

const formatDate = (iso) =>
  new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });

const formatEur = (cents) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format((cents || 0) / 100);

const ORDER_FILTERS = [
  { key: "all", label: "Tutti" },
  { key: "prepare", label: "Da preparare" },
  { key: "ready", label: "Pronti" },
  { key: "shipped", label: "Spediti" },
  { key: "pending", label: "In attesa" },
];

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadMessages = async () => {
    setLoading(true);
    try {
      const [{ data: msgs }, { data: ords }] = await Promise.all([
        api.get("/admin/contacts"),
        api.get("/admin/orders"),
      ]);
      setMessages(msgs);
      setOrders(ords);
    } catch {
      toast.error("Non riesco a caricare i dati. Riprova tra poco.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadCsv = () => {
    const rows = [["tipo", "data", "nome", "email", "telefono", "dettagli", "totale_eur", "stato"]];
    orders.forEach((o) =>
      rows.push([
        "ordine",
        formatDate(o.created_at),
        o.shipping?.name || "",
        o.customer_email || "",
        o.phone || "",
        (o.items || []).map((i) => `${i.name} x${i.quantity}`).join(" | "),
        ((o.amount || 0) / 100).toFixed(2).replace(".", ","),
        o.payment_status,
      ])
    );
    messages.forEach((m) =>
      rows.push(["messaggio", formatDate(m.created_at), m.name, m.email, "", (m.message || "").replace(/[\r\n]+/g, " "), "", ""])
    );
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "fatima-bio-cosmetic-ordini-messaggi.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("File scaricato: ordini e messaggi salvati");
  };

  const setShipped = async (sessionId, shipped) => {
    try {
      await api.post(`/admin/orders/${sessionId}/${shipped ? "ship" : "unship"}`);
      setOrders((prev) => prev.map((o) => (o.session_id === sessionId ? { ...o, fulfilled: shipped } : o)));
      toast.success(shipped ? "Ordine segnato come spedito" : "Segno Spedito rimosso");
    } catch {
      toast.error("Non sono riuscita ad aggiornare l'ordine. Riprova.");
    }
  };

  const setReady = async (sessionId, ready) => {
    try {
      await api.post(`/admin/orders/${sessionId}/${ready ? "ready" : "unready"}`);
      setOrders((prev) => prev.map((o) => (o.session_id === sessionId ? { ...o, ready } : o)));
      toast.success(ready ? "Ordine segnato come pronto" : "Segno Pronto rimosso");
    } catch {
      toast.error("Non sono riuscita ad aggiornare l'ordine. Riprova.");
    }
  };

  const copyShipping = async (o) => {
    const s = o.shipping || {};
    const text = [
      `Nome: ${s.name || ""}`,
      `Indirizzo: ${s.line1 || ""}${s.line2 ? `, ${s.line2}` : ""}`,
      `CAP: ${s.postal_code || ""}`,
      `Città: ${s.city || ""}${s.state ? ` (${s.state})` : ""}`,
      `Paese: ${s.country || ""}`,
      o.phone ? `Telefono: ${o.phone}` : null,
      o.customer_email ? `Email: ${o.customer_email}` : null,
    ].filter(Boolean).join("\n");
    const ok = () => toast.success("Dati spedizione copiati: incollali su paccofacile.it");
    try {
      await navigator.clipboard.writeText(text);
      ok();
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try {
        document.execCommand("copy");
        ok();
      } catch {
        toast.error("Non sono riuscita a copiare. Seleziona e copia manualmente.");
      }
      document.body.removeChild(ta);
    }
  };

  const now = new Date();
  const monthLabel = now.toLocaleString("it-IT", { month: "long", year: "numeric" });
  const monthOrders = orders.filter((o) => {
    const d = new Date(o.created_at);
    return o.payment_status === "paid" && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const statsRevenue = monthOrders.reduce((sum, o) => sum + (o.amount || 0), 0);

  const [orderFilter, setOrderFilter] = useState("all");
  const filteredOrders = orders.filter((o) => {
    if (orderFilter === "prepare") return o.payment_status === "paid" && !o.ready && !o.fulfilled;
    if (orderFilter === "ready") return o.ready && !o.fulfilled;
    if (orderFilter === "shipped") return o.fulfilled;
    if (orderFilter === "pending") return o.payment_status !== "paid";
    return true;
  });

  return (
    <main data-testid="admin-messages" className="py-14 sm:py-20">
      <div className="max-w-3xl mx-auto px-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl text-plum tracking-tight flex items-center gap-3">
              <Inbox className="w-7 h-7 text-lilac-dark" /> Messaggi delle clienti
            </h1>
            <p className="mt-2 text-sm text-plum/50" data-testid="messages-count">
              {messages.length} {messages.length === 1 ? "messaggio ricevuto" : "messaggi ricevuti"}
            </p>
          </div>
          <button
            data-testid="admin-export-btn"
            onClick={downloadCsv}
            className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.12em] text-matcha hover:text-plum transition-colors"
          >
            <Download className="w-4 h-4" /> Scarica tutto
          </button>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4" data-testid="monthly-stats">
          <div className="bg-white rounded-3xl border border-lilac-light p-5 sm:p-6">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-matcha">Ordini pagati · {monthLabel}</p>
            <p className="mt-2 font-serif text-3xl text-plum" data-testid="stats-count">{monthOrders.length}</p>
          </div>
          <div className="bg-white rounded-3xl border border-lilac-light p-5 sm:p-6">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-matcha">Fatturato · {monthLabel}</p>
            <p className="mt-2 font-serif text-3xl text-plum" data-testid="stats-revenue">{formatEur(statsRevenue)}</p>
          </div>
        </div>

        <div className="mt-10">
          <h2 className="font-serif text-2xl text-plum flex items-center gap-2">
            <ShoppingBag className="w-6 h-6 text-lilac-dark" /> Ordini ricevuti
          </h2>
          <p className="mt-1 text-sm text-plum/50" data-testid="orders-count">
            {orders.filter((o) => o.payment_status === "paid").length} pagati · {orders.length} totali
          </p>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="order-filters">
            {ORDER_FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`order-filter-${f.key}`}
                onClick={() => setOrderFilter(f.key)}
                className={`px-4 py-2 rounded-full text-[11px] font-bold uppercase tracking-[0.12em] transition-all duration-300 ${
                  orderFilter === f.key
                    ? "bg-lilac text-white shadow-[0_8px_24px_-8px_rgba(139,92,246,0.6)]"
                    : "bg-mist text-plum/70 hover:bg-lilac-soft hover:text-plum"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="mt-4 space-y-4">
            {!loading && filteredOrders.length === 0 && (
              <div className="bg-mist rounded-3xl p-10 text-center" data-testid="orders-empty">
                <p className="font-serif text-xl text-plum">
                  {orders.length === 0 ? "Nessun ordine per ora" : "Nessun ordine in questa vista"}
                </p>
                <p className="mt-2 text-sm text-plum/50">
                  {orders.length === 0 ? "Gli ordini delle clienti appariranno qui." : "Prova un altro filtro."}
                </p>
              </div>
            )}
            {filteredOrders.map((o, i) => (
              <Reveal key={o.session_id || i} delay={Math.min(i * 0.05, 0.3)}>
                <div className="bg-white rounded-3xl border border-lilac-light p-6" data-testid={`order-card-${i}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span
                      className={`text-[10px] font-bold uppercase tracking-[0.14em] px-3 py-1.5 rounded-full ${
                        o.fulfilled
                          ? "bg-plum text-white"
                          : o.ready
                            ? "bg-lilac text-white"
                            : o.payment_status === "paid"
                              ? "bg-matcha/15 text-matcha"
                              : "bg-lilac-soft text-plum/60"
                      }`}
                      data-testid={`order-status-${i}`}
                    >
                      {o.fulfilled ? "Spedito" : o.ready ? "Pronto" : o.payment_status === "paid" ? "Pagato" : "In attesa"}
                    </span>
                    <p className="text-xs text-plum/40">{formatDate(o.created_at)}</p>
                  </div>
                  <ul className="mt-3 space-y-1">
                    {(o.items || []).map((item, j) => (
                      <li key={j} className="text-sm text-plum/70">
                        {item.name} <span className="text-plum/40">× {item.quantity}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-3 font-serif text-lg text-plum" data-testid={`order-total-${i}`}>
                    Totale ordine: {formatEur(o.amount)}
                  </p>
                  {o.customer_email && (
                    <p className="mt-1 text-xs text-plum/50">Email cliente: {o.customer_email}</p>
                  )}
                  {o.phone && (
                    <p className="mt-1 text-xs text-plum/50 flex items-center gap-1.5" data-testid={`order-phone-${i}`}>
                      <Phone className="w-3.5 h-3.5" /> Telefono: {o.phone}
                    </p>
                  )}
                  {o.shipping && (
                    <p className="mt-2 text-xs text-plum/70 bg-mist rounded-xl px-4 py-2.5" data-testid={`order-shipping-${i}`}>
                      <span className="font-bold">Spedire a:</span> {o.shipping.name} · {o.shipping.line1}
                      {o.shipping.line2 ? `, ${o.shipping.line2}` : ""} · {o.shipping.postal_code} {o.shipping.city} ({o.shipping.country})
                    </p>
                  )}
                  {o.shipping && (
                    <button
                      data-testid={`order-copy-shipping-${i}`}
                      onClick={() => copyShipping(o)}
                      className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-lilac-dark hover:text-plum transition-colors"
                    >
                      <Copy className="w-3.5 h-3.5" /> Copia dati per paccofacile
                    </button>
                  )}
                  {o.payment_status === "paid" && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {o.fulfilled ? (
                        <button
                          data-testid={`order-unship-btn-${i}`}
                          onClick={() => setShipped(o.session_id, false)}
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-plum/15 text-plum/60 text-[11px] font-bold uppercase tracking-[0.12em] hover:border-plum hover:text-plum active:scale-95 transition-all duration-300"
                        >
                          <Truck className="w-4 h-4" /> Annulla spedito
                        </button>
                      ) : (
                        <>
                          {!o.ready && (
                            <button
                              data-testid={`order-ready-btn-${i}`}
                              onClick={() => setReady(o.session_id, true)}
                              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-lilac text-white text-[11px] font-bold uppercase tracking-[0.12em] hover:bg-lilac-dark active:scale-95 transition-all duration-300"
                            >
                              <Package className="w-4 h-4" /> Segna come pronto
                            </button>
                          )}
                          <button
                            data-testid={`order-ship-btn-${i}`}
                            onClick={() => setShipped(o.session_id, true)}
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-plum text-white text-[11px] font-bold uppercase tracking-[0.12em] hover:bg-plum-subtle active:scale-95 transition-all duration-300"
                          >
                            <Truck className="w-4 h-4" /> Segna come spedito
                          </button>
                          {o.ready && (
                            <button
                              data-testid={`order-unready-btn-${i}`}
                              onClick={() => setReady(o.session_id, false)}
                              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-plum/15 text-plum/60 text-[11px] font-bold uppercase tracking-[0.12em] hover:border-plum hover:text-plum active:scale-95 transition-all duration-300"
                            >
                              <Package className="w-4 h-4" /> Annulla pronto
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="mt-8 space-y-4">
          {loading && <p className="text-center text-plum/50 text-sm">Caricamento messaggi...</p>}
          {!loading && messages.length === 0 && (
            <div className="bg-mist rounded-3xl p-10 text-center" data-testid="messages-empty">
              <p className="font-serif text-xl text-plum">Nessun messaggio per ora</p>
              <p className="mt-2 text-sm text-plum/50">I messaggi inviati dal form contatti appariranno qui.</p>
            </div>
          )}
          {messages.map((m, i) => (
            <Reveal key={m.id || i} delay={Math.min(i * 0.05, 0.3)}>
              <div className="bg-white rounded-3xl border border-lilac-light p-6" data-testid={`message-card-${i}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-semibold text-plum">{m.name}</p>
                  <p className="text-xs text-plum/40">{formatDate(m.created_at)}</p>
                </div>
                <a
                  href={`mailto:${m.email}`}
                  data-testid={`message-email-${i}`}
                  className="mt-1 inline-flex items-center gap-1.5 text-sm text-lilac-dark hover:text-plum transition-colors"
                >
                  <Mail className="w-3.5 h-3.5" /> {m.email}
                </a>
                <p className="mt-3 text-sm sm:text-base text-plum/70 leading-relaxed whitespace-pre-line">{m.message}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </main>
  );
}
